import { api, ApiError, UNAUTHORIZED_EVENT, type PlaybackStart } from '../api.ts';
import { afterHistorySettled, popOverlay, pushOverlay } from '../router.ts';
import { isPrematureEnd } from '../../shared/playback.ts';
import { el, formatDuration, mount, onTap } from '../ui/dom.ts';
import { confirmDialog, stateBlock, toast } from '../ui/feedback.ts';
import { iconEl } from '../ui/icons.ts';
import { formatQuotaLeft } from '../ui/shell.ts';
import { isCar, isPhone, isTeslaLike } from '../ui/surface.ts';
import { state, setQuality, updateQuota } from '../state.ts';
import { track } from '../stats.ts';
import { t } from '../i18n/index.ts';

declare const MP4Box: any;
declare const DataStream: any;

// =============================================================================
// Oynatıcı — canvas-play'in (v51) WebCodecs yolundan taşındı.
//
// Neden <video> değil: araç kiosk'unda <video> ekranı karartıyordu. Video H.264
// olarak kopyalanıp parçalı MP4 hâlinde geliyor, mp4box.js demux ediyor,
// VideoDecoder çözüyor, kareler tuvale çiziliyor.
//
// Neden ses de WebCodecs + Web Audio: <audio> öğesinin currentTime'ı akıştan sonra
// duyulan sesin ÖNÜNE geçiyordu; her karesi o saate göre çizilen görüntü de
// değişken biçimde geri kalıyordu. Burada AAC'yi uygulama çözüyor ve
// AudioContext'e KENDİ içerik zamanıyla yerleştiriyor: oynatma saati artık tam
// olarak zamanladığımız ses.
//
// Korunan kurallar:
//  * Ses kapısı (audioGate): ilk video karesi tuvale gelene kadar ses başlamaz.
//  * Video yastığı ÇÖZÜLMÜŞ kare değil, KODLU parça olarak tutulur; her canlı
//    VideoFrame kod çözücünün havuzundan yer kapar ve saniyelerce tutmak
//    çözücüyü tıkar.
//  * İstek nesli (generation): eski bir seek/fetch yanıtı yeni oturuma karışamaz.
//  * Sekme gizlenmesi / ekran uykusu sonrası yeniden çapa (hardResync).
// =============================================================================

const AUDIO_TARGET_LEAD = 2.5;      // saniye: saatin bu kadar ilerisine ses planlanır
const AUDIO_MAX_PENDING = 300;      // çözülmüş ses karesi tavanı (geri basınç)
const VIDEO_LEAD_MS = 6000;         // ses saatinin ilerisinde tutulan kodlu video
const PREBUFFER_LEAD_MS = 20000;    // saat başlamadan önce izin verilen yastık
const MAX_PREBUFFER_BYTES = 8 * 1024 * 1024;
const MAX_LIVE_FRAMES = 8;          // aynı anda yaşayan çözülmüş kare
const MAX_QUEUED_FRAMES = 24;
const RESYNC_BEHIND_MS = 600;
const STALL_MS = 2500;
const SUSPEND_GAP_MS = 1500;
const SKIP_STEP = 10;
const SKIP_COALESCE_MS = 450;
const SKIP_ZONE = 0.3;
// İstek zaman aşımları: yanıt vermeyen bağlantı perdeyi süresiz tutmasın.
// /start cömert: kaynak çözümü (yt-dlp) ve devir beklemesi uzun sürebilir.
const START_TIMEOUT_MS = 60_000;
// Seek'te sunucu anahtar kare sondasını iki kez, 12'şer saniye deneyebilir.
const SEEK_TIMEOUT_MS = 30_000;
const BEAT_TIMEOUT_MS = 10_000;
// Başarısız seek'in yeniden deneme aralıkları (ms); tükenince "Tekrar deneyin" ekranı.
const SEEK_RETRY_MS = [1000, 2000, 4000];

export interface PlaybackTarget {
  source:
    | { type: 'youtube'; videoId: string; live?: boolean; title?: string; channel?: string; durationSec?: number | null }
    | { type: 'tv'; channelId: string }
    | { type: 'iptv'; itemId: string }
    | { type: 'test'; live?: boolean };
  title: string;
  subtitle?: string;
  /** Oynatıcıdan çıkınca dönülecek yol. */
  returnTo?: string;
  /** Canlıda kanal değiştirme menüsü için. */
  channelList?: Array<{ id: string; name: string; target: PlaybackTarget }>;
  /** Video bitince "şunu da izle" için. */
  relatedQuery?: { exclude: string; channel: string; title: string };
  onRelated?: (container: HTMLElement, close: () => void) => void;
  startAt?: number;
}

interface Ui {
  root: HTMLElement;
  canvas: HTMLCanvasElement;
  ctx: CanvasRenderingContext2D;
  veil: HTMLElement;
  veilText: HTMLElement;
  title: HTMLElement;
  subtitle: HTMLElement;
  quota: HTMLElement;
  rotate: HTMLElement;
  netHint: HTMLElement;
  controls: HTMLElement;
  playBtn: HTMLButtonElement;
  seek: HTMLInputElement;
  timeNow: HTMLElement;
  timeEnd: HTMLElement;
  volume: HTMLInputElement;
  qualityBtn: HTMLButtonElement;
  channelBtn: HTMLButtonElement;
  stats: HTMLElement;
  skipHint: HTMLElement;
  overlay: HTMLElement;
  menuHost: HTMLElement;
}

let ui: Ui | null = null;
let target: PlaybackTarget | null = null;
let onClose: (() => void) | null = null;

// --- Oturum ------------------------------------------------------------------
let sessionId: string | null = null;
let mode: 'live' | 'vod' | null = null;
let itemKey = '';
let durationSec = 0;
let baseOffsetSec = 0;
let generation = 0;
let heartbeatTimer: number | null = null;
let idleTimer: number | null = null;
let closing = false;
// Sunucu oturumu kapattı (kaynak bitti) ama tampondaki içerik hâlâ oynuyor.
// ffmpeg VOD'u gerçek zamanın 4 katı hızla okuduğu için kısa videoda ve her
// videonun son bölümünde izleyiciden çok önce biter; bu, oynatmanın bitişi değil.
let serverEnded = false;
// Bu oturumda sunucudan yeniden kodlamalı (emniyetli) seek isteniyor mu.
let safeSeek = false;
// HEVC: cihaz çözebiliyorsa sunucu HEVC kaynağı yeniden kodlamadan gönderir. Bu
// oturumda çözücü HEVC'de hata verdiyse bir daha istenmez (sunucu H.264'e çevirir).
let hevcOff = false;
let hevcProbe: Promise<boolean> | null = null;
let streamIsHevc = false;

function clientHevc(): Promise<boolean> {
  if (hevcOff) return Promise.resolve(false);
  hevcProbe ??= (async () => {
    try {
      const support = await VideoDecoder.isConfigSupported({ codec: 'hvc1.1.6.L123.B0', codedWidth: 1920, codedHeight: 1080 });
      return support.supported === true;
    } catch { return false; }
  })();
  return hevcProbe;
}

/** HEVC akışı çözülemedi: oturumu H.264 (yeniden kodlama) ile yeniden kur. */
function retryWithoutHevc(reason: string): boolean {
  if (!streamIsHevc || hevcOff || closing) return false;
  hevcOff = true;
  track('player_error', { ...playbackRef(), data: { message: 'hevc_fallback: ' + reason.slice(0, 160) } });
  const next = resumeTarget();
  if (!next) return false;
  void start(next, false);
  return true;
}
// Yolda olan (ya da yeniden denenen) seek'in hedefi. Seek sürerken akış yıkılmış,
// saat 0 ve `baseOffsetSec` hâlâ eski tabandır; konumu o ikisinden hesaplayan her
// şey (atlama, kalp atışı, kapanıştaki kayıt, kalite değişimi) eski yere düşüyordu.
let pendingSeekSec: number | null = null;
// Seek beklenirken oynat/duraklat düğmesine basıldıysa son niyet (null: basılmadı).
let seekIntent: boolean | null = null;
// Oynatıcı için tarayıcı geçmişine girdi eklendi mi (Geri tuşu kapatsın diye).
let historyHeld = false;
// Oynatıcı açılmadan önce odakta olan öğe; kapanınca odak ona döner.
let lastFocus: HTMLElement | null = null;
// Ekran uyanık tutma kilidi: <canvas> + Web Audio tarayıcı için "video" değil,
// ekran kendi zaman aşımında kararıyordu.
let wakeLock: any = null;
let wakeLockPending = false;

/** Tek konum kaynağı (saniye). Seek yoldayken hedefi, değilse taban + ses saati. */
function positionSec(): number {
  if (pendingSeekSec !== null) return pendingSeekSec;
  return baseOffsetSec + audioClockSec();
}

// --- Ses ---------------------------------------------------------------------
let audioCtx: AudioContext | null = null;
let audioGain: GainNode | null = null;
let audioVolume = 1;
let audioDecoder: AudioDecoder | null = null;
let audioMp4: any = null;
let audioAbort: AbortController | null = null;
let audioSources: AudioBufferSourceNode[] = [];
let audioBaseTs: number | null = null;
// Kopyalanan seste ilk örneğin içerik zamanı (sesin görüntüden ne kadar geç başladığı).
let audioLeadSec = 0;
let audioStartCtxSec = 0;
let audioClockRunning = false;
let audioPaused = false;
let audioPausedAt = 0;
let pendingAudio: AudioData[] = [];
let audioFetchDone = false;
let audioEndedFired = false;
let audioGate = false;
let audioWantsPlay = false;

// --- Video -------------------------------------------------------------------
let videoAbort: AbortController | null = null;
let videoDecoder: VideoDecoder | null = null;
let videoMp4: any = null;
let frameQueue: Array<{ pts: number; frame: VideoFrame }> = [];
let pendingChunks: EncodedVideoChunk[] = [];
let basePtsUs: number | null = null;
let rafId = 0;
let lastDrawWall = 0;
let lastDrawPts: number | null = null;
let lastProgressWall = 0;
let lastProgressPts = -1;
let lastDrawLagMs = 0;
let maxDrawGapMs = 0;
let drawnFrames = 0;
let resyncs = 0;
let scrubbing = false;
// Çizim tasarrufu: aynı kare yeniden çizilmez, süre etiketi yalnız değişince yazılır.
// `forcePaint`: tuval temizlendi/akış yeniden kuruldu, sıradaki kare mutlaka çizilsin.
let forcePaint = true;
let lastSeekPaint = -1;
let lastTimePaint = -1;
// Art arda (ilerleme olmadan) sayılan takılmalar; canlıda yeniden bağlanma eşiği.
let staleStalls = 0;
// Akış bağlantısı, oynatma beklerken (duraklatılmış/gizli) koptu; devamda yeniden kurulur.
let streamLost = false;
// Kullanıcının duraklat düğmesine bastığı an (0: duraklatılmadı ya da sekme gizlendi).
let userPausedAt = 0;
// Bu süreden kısa duraklatmadan sonra akış yerinde sürdürülür. Daha uzununda
// vekil sunucu okunmayan yanıtı kapatmış olabilir (nginx send_timeout 60 sn);
// o zaman eski yol: sunucuda aynı konuma yeniden çapalama.
const FAST_RESUME_MAX_MS = 45_000;

// --- İleri/geri alma ---------------------------------------------------------
let pendingSkip = 0;
let skipBase = 0;
let skipTimer: number | null = null;
let skipCommitted: number | null = null;
// Atlama sıra numarası: geçersiz kalmış bir atlamanın devamı, yenisinin
// `skipCommitted` değerini silmesin.
let skipSeq = 0;

let statsOn = false;
let statsAt = 0;
let statsFrames = 0;

// İstatistik sayaçları: oturum başına takılma, duraklatma; kalp atışıyla
// sunucuya gider ve bitişte play_end olayına yazılır.
let stallCount = 0;
let pauseCount = 0;
let stallStartedAt = 0;

// Donma teşhisi (shared/netdiag.ts). İstemci yalnız ölçer; nedeni sunucu,
// kendi gönderdiği baytla karşılaştırarak kalp atışında söyler.
const NET_HINT_GAP_MS = 60_000;     // uyarı en sık dakikada bir
const NET_HINT_SHOW_MS = 9000;
let netEpoch = 0;                   // sunucunun bu video yanıtına verdiği sıra numarası
let netStreamAt = 0;
let netRxBytes = 0;
let netContentSec = 0;
let netVideoDone = false;           // video akışı temiz biçimde sonuna kadar alındı
let videoFetchOver = false;         // video akışından artık bayt gelmeyecek (bitti ya da koptu)
let netMinLeadMs = Infinity;        // son kalp atışından beri görülen en düşük yastık
let netSteady = true;               // pencere boyunca kesintisiz oynatma
let netGateAt = 0;                  // perdenin (ilk kare bekleniyor) görünür olduğu an
let netStallsSent = 0;
let netBeatFails = 0;
let netRttMs = 0;
let netHintAt = 0;
let netHintTimer: number | null = null;

function playbackRef() {
  return { playbackId: sessionId, kind: target?.source.type ?? null, itemKey, title: target?.title ?? null, quality: state.quality };
}

export function isPlaying(): boolean {
  return sessionId !== null;
}

// =============================== Ses yolu ====================================

function ensureAudioCtx(): void {
  if (!audioCtx) {
    audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
    audioGain = audioCtx.createGain();
    audioGain.gain.value = audioVolume;
    audioGain.connect(audioCtx.destination);
  }
  if (audioCtx.state === 'suspended') void audioCtx.resume().catch(() => {});
}

/** Gerçekten çalınmış ses saniyesi (seek noktasından 0 tabanlı). */
function audioClockSec(): number {
  if (!audioClockRunning || !audioCtx) return 0;
  if (audioPaused) return audioPausedAt;
  return Math.max(0, audioCtx.currentTime - audioStartCtxSec);
}

function audioIsPaused(): boolean {
  return audioPaused || !audioClockRunning;
}

function pauseAudio(): void {
  if (!audioClockRunning || audioPaused) return;
  audioPausedAt = audioClockSec();
  audioPaused = true;
  void audioCtx?.suspend().catch(() => {});
}

function resumeAudio(): void {
  ensureAudioCtx();
  audioPaused = false;
  void audioCtx?.resume().catch(() => {});
}

/**
 * Bir çözülmüş parçayı SIRAYLA değil, KENDİ içerik zamanında planla. Çapa sabittir:
 * geciken parça yerini korur ve düşürülür — çapayı kaydırmak saatin kaymasıydı.
 */
function scheduleAudio(data: AudioData): void {
  if (!audioCtx || !audioGain) { data.close(); return; }
  const contentSec = data.timestamp / 1e6;
  if (!audioClockRunning) {
    // Saat içerik zamanı sıfırdan başlar; ses geç başlıyorsa o süre sessiz geçer.
    audioStartCtxSec = audioCtx.currentTime + 0.15 - (contentSec - audioLeadSec);
    audioClockRunning = true;
  }
  const startAt = audioStartCtxSec + contentSec;
  if (startAt < audioCtx.currentTime + 0.01) { data.close(); return; }
  const channels = data.numberOfChannels;
  const frames = data.numberOfFrames;
  const buffer = audioCtx.createBuffer(channels, frames, data.sampleRate);
  const tmp = new Float32Array(frames);
  for (let c = 0; c < channels; c++) {
    try { data.copyTo(tmp, { planeIndex: c, format: 'f32-planar' }); }
    catch { try { data.copyTo(tmp, { planeIndex: c }); } catch { /* biçim desteklenmiyor */ } }
    buffer.getChannelData(c).set(tmp);
  }
  const source = audioCtx.createBufferSource();
  source.buffer = buffer;
  source.connect(audioGain);
  source.onended = () => {
    const index = audioSources.indexOf(source);
    if (index >= 0) audioSources.splice(index, 1);
  };
  try { source.start(startAt); } catch { /* bağlam kapandı */ }
  audioSources.push(source);
  data.close();
}

/** Saatin sınırlı kadar ilerisine planla; gerisi pendingAudio'da ucuza bekler. */
function pumpAudio(): void {
  if (audioGate || !audioCtx || !audioWantsPlay || audioPaused || document.hidden) return;
  const horizon = (audioClockRunning ? audioClockSec() : 0) + AUDIO_TARGET_LEAD;
  while (pendingAudio.length) {
    if (audioClockRunning && pendingAudio[0].timestamp / 1e6 > horizon) break;
    scheduleAudio(pendingAudio.shift()!);
  }
}

function stopAudioSources(): void {
  for (const source of audioSources) {
    try { source.onended = null; source.stop(); source.disconnect(); } catch { /* durmuş */ }
  }
  audioSources = [];
}

function teardownAudio(): void {
  if (audioAbort) { try { audioAbort.abort(); } catch { /* zaten iptal */ } audioAbort = null; }
  stopAudioSources();
  if (audioDecoder) { try { audioDecoder.close(); } catch { /* kapalı */ } audioDecoder = null; }
  if (audioMp4) { try { audioMp4.stop(); } catch { /* kapalı */ } audioMp4 = null; }
  for (const data of pendingAudio) { try { data.close(); } catch { /* kapalı */ } }
  pendingAudio = [];
  audioBaseTs = null;
  audioLeadSec = 0;
  audioClockRunning = false;
  audioPaused = false;
  // Duraklatma konumu bu akışa aitti; sıfırlanmazsa bir sonraki videoya sızıyordu.
  audioPausedAt = 0;
  audioFetchDone = false;
  audioEndedFired = false;
}

// --- Yayın akışının adresi ---------------------------------------------------
// Sunucu ayrı bir yayın adresi verdiyse akış oradan çekilir: o adres
// Cloudflare'den geçmez ve yayın gerçek hızında gelir. O adrese ulaşılamazsa
// (kapalı port, kısıtlı ağ) site içi yola dönülür ve bu sekmede bir daha denenmez.
let directMedia: { base: string; key: string } | null = null;
const DIRECT_OFF_KEY = 'akis.direct-media-off';
const DIRECT_CONNECT_MS = 5000;

function directAllowed(): boolean {
  try { return sessionStorage.getItem(DIRECT_OFF_KEY) !== '1'; } catch { return true; }
}

function mediaUrl(id: string, track: 'video' | 'audio', bust = false): string {
  const tail = bust ? `_=${Date.now()}` : '';
  if (directMedia && directAllowed()) {
    return `${directMedia.base}/api/stream/${id}/${track}?k=${encodeURIComponent(directMedia.key)}${tail ? '&' + tail : ''}`;
  }
  return `/api/playback/${id}/${track}${tail ? '?' + tail : ''}`;
}

async function fetchMedia(url: string, signal: AbortSignal): Promise<Response> {
  if (!/^https?:/.test(url)) return fetch(url, { signal });
  const attempt = new AbortController();
  const forward = () => attempt.abort();
  signal.addEventListener('abort', forward, { once: true });
  const timer = window.setTimeout(() => attempt.abort(), DIRECT_CONNECT_MS);
  try {
    const response = await fetch(url, { signal: attempt.signal });
    window.clearTimeout(timer);
    // 409 (ses akışı meşgul) gerçek bir yanıttır; site içi yol da aynısını verirdi.
    if ((response.ok && response.body) || response.status === 409) return response;
  } catch (err) {
    window.clearTimeout(timer);
    if (signal.aborted) throw err;
  }
  signal.removeEventListener('abort', forward);
  try { sessionStorage.setItem(DIRECT_OFF_KEY, '1'); } catch { /* depolama kapalı */ }
  const parsed = new URL(url);
  const match = parsed.pathname.match(/\/api\/stream\/([^/]+)\/(video|audio)$/);
  const bust = parsed.searchParams.get('_');
  return fetch(`/api/playback/${match?.[1]}/${match?.[2]}${bust ? `?_=${bust}` : ''}`, { signal });
}

function startAudio(url: string): void {
  ensureAudioCtx();
  teardownAudio();
  if (audioGain) audioGain.gain.value = audioVolume;
  // Saat, ilk video karesi ekrana gelene kadar TUTULUR: ses ve görüntü birlikte
  // başlasın. Zaman aşımıyla serbest bırakmak, görüntüsüz sesi başlatmak olurdu.
  audioGate = true;
  netGateAt = performance.now();
  audioWantsPlay = true;
  void streamAudio(url);
}

async function streamAudio(url: string): Promise<void> {
  const controller = new AbortController();
  audioAbort = controller;
  const active = () => audioAbort === controller && !controller.signal.aborted;
  const mp4 = MP4Box.createFile();
  audioMp4 = mp4;
  mp4.onError = () => {};
  mp4.onReady = (info: any) => {
    if (!active()) return;
    const track = info.audioTracks && info.audioTracks[0];
    if (!track) return;
    let description: Uint8Array | undefined;
    // Sunucu sesi yeniden kodlamadan kopyaladığında, ses görüntüden geç
    // başlıyorsa aradaki fark başlıkta boş bir düzenleme (elst, media_time -1)
    // olarak gelir. Örneklerin zamanı sıfırdan başladığı için fark buradan
    // eklenir; yoksa ses o kadar erken çalardı. Yeniden kodlanan seste sunucu
    // boşluğu sessizlikle doldurur ve düzenleme gelmez.
    let leadUs = 0;
    try {
      const trak = mp4.getTrackById(track.id);
      const edit = trak.edts?.elst?.entries?.[0];
      if (edit && edit.media_time === -1 && info.timescale > 0) {
        const lead = (edit.segment_duration * 1e6) / info.timescale;
        if (lead > 0 && lead < 30e6) { leadUs = lead; audioLeadSec = lead / 1e6; }
      }
      for (const entry of trak.mdia.minf.stbl.stsd.entries) {
        const esds = entry.esds || (entry.mp4a && entry.mp4a.esds);
        const asc = esds?.esd?.descs?.[0]?.descs?.[0]?.data;
        if (asc) { description = asc; break; }
      }
    } catch { /* bazı yapılar AAC-LC'yi açıklamasız çözer */ }
    const config: AudioDecoderConfig = {
      codec: track.codec,
      sampleRate: track.audio.sample_rate,
      numberOfChannels: track.audio.channel_count,
    };
    if (description) config.description = description;
    // Çözücü tek bir bozuk örnekte kalıcı olarak kapanır. Kopyalanan seste kaynağın
    // bozuk bir paketi buraya kadar gelebilir; çözücü yeniden kurulmazsa yayının
    // geri kalanı sessiz akardı. Sınırlı sayıda yeniden kurulur.
    let rebuilds = 0;
    const makeDecoder = (): AudioDecoder | null => {
      const next = new AudioDecoder({
        output: (data) => { if (active()) pendingAudio.push(data); else data.close(); },
        error: () => {
          if (!active() || audioDecoder !== next || rebuilds >= 30) return;
          rebuilds += 1;
          audioDecoder = makeDecoder();
        },
      });
      try { next.configure(config); } catch { return null; }
      return next;
    };
    const first = makeDecoder();
    if (!first) return;
    audioDecoder = first;
    mp4.setExtractionOptions(track.id, null, { nbSamples: 50 });
    mp4.onSamples = (_id: number, _user: unknown, samples: any[]) => {
      if (!active()) return;
      for (const sample of samples) {
        const ts = (sample.cts * 1e6) / sample.timescale;
        if (audioBaseTs === null) audioBaseTs = ts;
        try {
          audioDecoder?.decode(new EncodedAudioChunk({
            type: 'key',
            timestamp: ts - audioBaseTs + leadUs,
            duration: (sample.duration * 1e6) / sample.timescale,
            data: sample.data,
          }));
        } catch { /* çözücü kapandı */ }
      }
      releaseSamples(mp4, _id, samples);
    };
    mp4.start();
  };

  // Ses, oynatma saatinin kendisi: akışı alınamazsa saat hiç başlamaz ve ekran
  // ilk karede, hatasız donar. Bu yüzden sessizce dönülmez; kopma gibi ele alınır.
  let response: Response;
  try { response = await fetchMedia(url, controller.signal); }
  catch { if (active()) onStreamLost(); return; }
  if (!active()) return;
  if (!response.ok || !response.body) { onStreamLost(); return; }
  const reader = response.body.getReader();
  let offset = 0;
  // Temiz bitiş (sunucu yanıtı kapattı) ile kopma (okuma hata verdi) ayrı şeyler:
  // kopmayı "ses bitti" saymak, süresi bilinmeyen içerikte öneri ekranını açıyordu.
  let clean = false;
  try {
    while (true) {
      // Geri basınç: planlanandan çok ileri çözdüysek okumayı durdur ki sunucudaki
      // ses ffmpeg'i beklesin, biz tüm parçayı belleğe almayalım.
      while (active() && pendingAudio.length > AUDIO_MAX_PENDING) {
        await sleep(60);
      }
      if (!active()) return;
      const { done, value } = await reader.read();
      if (!active()) return;
      if (done) { clean = true; break; }
      const ab = value.buffer.slice(value.byteOffset, value.byteOffset + value.byteLength) as any;
      ab.fileStart = offset;
      offset += ab.byteLength;
      mp4.appendBuffer(ab);
    }
  } catch { /* iptal veya kopma */ }
  if (!active()) return;
  if (!clean) { onStreamLost(); return; }
  if (active()) {
    mp4.flush();
    if (audioDecoder && audioDecoder.state === 'configured') {
      try { await audioDecoder.flush(); } catch { /* kapandı */ }
    }
    if (active()) audioFetchDone = true;
  }
}

function releaseAudioGate(): void {
  if (!audioGate) return;
  audioGate = false;
  if (ui) ui.veil.hidden = true;
  // Gizlenme sayacı oynatıcı AÇILIRKEN başlıyordu; bağlanma + tamponlama
  // 4 saniyeden uzun sürdüğü için kullanıcının gördüğü ilk karede kontroller
  // çoktan kaybolmuş oluyordu. Masaüstünde fare uyandırıyor, dokunmatikte
  // hiçbir şey uyandırmıyordu. Sayaç görüntüyle birlikte başlasın.
  resetIdle();
  maybeRotateHint();
  maybeDriveWarning();
  if (!audioWantsPlay) { setPlayIcon(false); return; }
  ensureAudioCtx();
  pumpAudio();
  setPlayIcon(true);
  void acquireWakeLock();
}

/**
 * mp4box, verdiği her örneğin kopyasını `trak.samples[i].data` içinde saklar;
 * bırakılmazsa izlenen akışın TAMAMI bellekte kalır (720p'de saatte ~1 GB) ve
 * uzun oynatmada sekme çöker. Parçalar `Encoded*Chunk`'a kopyalandıktan sonra
 * güvenle bırakılır.
 */
function releaseSamples(mp4: any, trackId: number, samples: any[]): void {
  if (!samples.length) return;
  try { mp4.releaseUsedSamples(trackId, samples[samples.length - 1].number + 1); }
  catch { /* eski mp4box ya da kapanmış dosya */ }
}

// --- Ekranı uyanık tut ---------------------------------------------------------

async function acquireWakeLock(): Promise<void> {
  if (wakeLock || wakeLockPending || document.hidden) return;
  const locks = (navigator as any).wakeLock;
  if (!locks || typeof locks.request !== 'function') return;
  wakeLockPending = true;
  try {
    const lock = await locks.request('screen');
    // Beklerken oynatma durduysa kilidi tutma.
    if (!sessionId || !audioWantsPlay) { try { await lock.release(); } catch { /* bırakılmış */ } return; }
    wakeLock = lock;
    lock.addEventListener?.('release', () => { if (wakeLock === lock) wakeLock = null; });
  } catch { /* izin yok, pil tasarrufu, desteklenmiyor */ }
  finally { wakeLockPending = false; }
}

function releaseWakeLock(): void {
  const lock = wakeLock;
  wakeLock = null;
  if (!lock) return;
  try { void lock.release?.()?.catch?.(() => {}); } catch { /* bırakılmış */ }
}

// ============================== Video yolu ===================================

/**
 * Kanca: seek SONRASI kurulan video akışında çözücü hata verdi. Şimdilik boş;
 * `true` dönerse hata ele alınmış sayılır ve "açılamadı" ekranı gösterilmez
 * (ör. aynı konuma emniyetli/yeniden kodlamalı seek ile yeniden denemek için).
 * Tek çağrı noktası: `streamVideo` içindeki VideoDecoder `error` geri çağrısı.
 */
function onDecodeErrorAfterSeek(err: unknown): boolean {
  // Sunucu seek sonrası videoyu kopyalar (yeniden kodlamaz). Akışın ortasından
  // başlayan kopyayı çözücü kabul etmezse (açık GOP'lu kaynak) aynı konuma bir
  // kez "emniyetli" seek istenir: sunucu bu oturumda yeniden kodlamaya döner.
  if (safeSeek || mode !== 'vod' || !sessionId) return false;
  safeSeek = true;
  track('player_error', { ...playbackRef(), data: { message: 'seek_copy_decode: ' + String((err as Error)?.message ?? err).slice(0, 160) } });
  void seekTo(baseOffsetSec);
  return true;
}

async function streamVideo(url: string, afterSeek = false): Promise<void> {
  const controller = new AbortController();
  videoAbort = controller;
  const active = () => videoAbort === controller && !controller.signal.aborted;
  frameQueue = [];
  pendingChunks = [];
  basePtsUs = null;
  netEpoch = 0;
  netStreamAt = performance.now();
  netRxBytes = 0;
  netContentSec = 0;
  netVideoDone = false;
  videoFetchOver = false;

  videoDecoder = new VideoDecoder({
    output: (frame) => {
      if (!active()) { frame.close(); return; }
      const pts = (frame.timestamp - (basePtsUs ?? 0)) / 1000;
      frameQueue.push({ pts, frame });
      // Emniyet valfi: EN YENİ kareyi at, baştakini asla — baştaki çizilmek üzere.
      while (frameQueue.length > MAX_QUEUED_FRAMES) frameQueue.pop()!.frame.close();
      pumpDecoder();
    },
    error: (err) => {
      if (!active()) return;
      if (retryWithoutHevc(err.message)) return;
      // "Kaldığınız yerden devam" da akışın ortasından başlar (baseOffsetSec > 0).
      if ((afterSeek || baseOffsetSec > 0) && onDecodeErrorAfterSeek(err)) return;
      showFatal(t('player.decodeErr', { msg: err.message }));
    },
  });

  const mp4 = MP4Box.createFile();
  videoMp4 = mp4;
  mp4.onError = (err: unknown) => { if (active()) showFatal(t('player.parseErr', { msg: String(err) })); };
  mp4.onReady = (info: any) => {
    if (!active()) return;
    const track = info.videoTracks[0];
    if (!track) { showFatal(t('player.noTrack')); return; }
    const trak = mp4.getTrackById(track.id);
    let description: Uint8Array | undefined;
    for (const entry of trak.mdia.minf.stbl.stsd.entries) {
      const box = entry.avcC || entry.hvcC;
      if (box) {
        const stream = new DataStream(undefined, 0, DataStream.BIG_ENDIAN);
        box.write(stream);
        description = new Uint8Array(stream.buffer, 8);
        break;
      }
    }
    try {
      videoDecoder!.configure({
        codec: track.codec,
        description,
        codedWidth: track.track_width,
        codedHeight: track.track_height,
      });
    } catch (err) {
      if (retryWithoutHevc((err as Error).message)) return;
      showFatal(t('player.unsupported', { msg: (err as Error).message }));
      return;
    }
    mp4.setExtractionOptions(track.id, null, { nbSamples: 50 });
    mp4.onSamples = (_id: number, _user: unknown, samples: any[]) => {
      if (!active()) return;
      for (const sample of samples) {
        const timestamp = (sample.cts * 1e6) / sample.timescale;
        if (basePtsUs === null) basePtsUs = timestamp;
        netContentSec = Math.max(netContentSec, (timestamp - basePtsUs) / 1e6 + sample.duration / sample.timescale);
        pendingChunks.push(new EncodedVideoChunk({
          type: sample.is_sync ? 'key' : 'delta',
          timestamp,
          duration: (sample.duration * 1e6) / sample.timescale,
          data: sample.data,
        }));
      }
      releaseSamples(mp4, _id, samples);
      pumpDecoder();
    };
    mp4.start();
  };

  let response: Response;
  // Bağlantı hiç kurulamadıysa kopma gibi ele alınır; sessizce dönmek perdeyi
  // ("hazırlanıyor") süresiz açık bırakıyordu.
  try { response = await fetchMedia(url, controller.signal); }
  catch { if (active()) { videoFetchOver = true; onStreamLost(); } return; }
  if (!active()) return;
  if (!response.ok || !response.body) { showFatal(t('player.streamFail')); return; }
  netEpoch = Number(response.headers.get('X-Stream-Epoch')) || 0;
  const reader = response.body.getReader();
  let offset = 0;
  // Ses saati kalkana kadar cömert oku; sonra küçük yastık tut. readyState tek
  // yönlü DEĞİL: tampon boşalmasında geri düşer, o yüzden "ses kalktı" mandallanır.
  let audioUp = false;
  const holdOff = () => {
    if (!audioIsPaused() && audioClockRunning) audioUp = true;
    if (audioUp) return leadMs() > VIDEO_LEAD_MS;
    return leadMs() > PREBUFFER_LEAD_MS || offset > MAX_PREBUFFER_BYTES;
  };
  try {
    while (true) {
      while (active() && holdOff()) await sleep(120);
      if (!active()) return;
      const { done, value } = await reader.read();
      if (!active()) return;
      // Temiz bitişte takılma bekçisi susar; kopmada (catch) susmaz ki yeniden çapalasın.
      if (done) { netVideoDone = true; break; }
      const ab = value.buffer.slice(value.byteOffset, value.byteOffset + value.byteLength) as any;
      ab.fileStart = offset;
      offset += ab.byteLength;
      netRxBytes = offset;
      mp4.appendBuffer(ab);
    }
  } catch { /* iptal veya kopma */ }
  if (!active()) return;
  videoFetchOver = true;
  // Akış temiz bitmediyse bağlantı kopmuştur: kendi kendine düzelmez, yeniden
  // bağlanılır. (Eskiden canlıda görüntü son karede, hatasız kalıyordu.)
  if (!netVideoDone) onStreamLost();
}

function pumpDecoder(): void {
  if (!videoDecoder || videoDecoder.state !== 'configured') return;
  while (pendingChunks.length && frameQueue.length + videoDecoder.decodeQueueSize < MAX_LIVE_FRAMES) {
    videoDecoder.decode(pendingChunks.shift()!);
  }
}

/** Çizim noktasının (ses saati) ilerisinde tutulan video, ms. */
function leadMs(): number {
  const t = audioClockSec() * 1000;
  if (pendingChunks.length && basePtsUs !== null) {
    return (pendingChunks[pendingChunks.length - 1].timestamp - basePtsUs) / 1000 - t;
  }
  if (frameQueue.length) return frameQueue[frameQueue.length - 1].pts - t;
  return 0;
}

function resyncForward(tMs: number): boolean {
  if (basePtsUs === null) return false;
  let cut = -1;
  for (let i = 0; i < pendingChunks.length; i++) {
    if ((pendingChunks[i].timestamp - basePtsUs) / 1000 > tMs) break;
    if (pendingChunks[i].type === 'key') cut = i;
  }
  if (cut <= 0) return false;
  pendingChunks.splice(0, cut);
  for (const item of frameQueue) { try { item.frame.close(); } catch { /* kapalı */ } }
  frameQueue = [];
  resyncs += 1;
  return true;
}

function resyncIfBehind(): void {
  if (!pendingChunks.length || basePtsUs === null) return;
  const tMs = audioClockSec() * 1000;
  const newest = frameQueue.length ? frameQueue[frameQueue.length - 1].pts : -Infinity;
  if (newest > tMs - RESYNC_BEHIND_MS) return;
  resyncForward(tMs);
}

/**
 * Çizim döngüsü dondu (sekme gizlendi, ekran uyudu, uzun bir takılma) ama ses
 * saati akmaya devam etti. Elimizdeki video saate yetişiyorsa oraya atla;
 * yetişmiyorsa dürüst tek çözüm sunucuyu o konuma yeniden çapalamaktır.
 */
function hardResync(): void {
  if (basePtsUs === null) return;
  const tMs = audioClockSec() * 1000;
  const have = pendingChunks.length
    ? (pendingChunks[pendingChunks.length - 1].timestamp - basePtsUs) / 1000
    : (frameQueue.length ? frameQueue[frameQueue.length - 1].pts : -Infinity);
  if (have >= tMs - 500) { resyncForward(tMs); return; }
  if (mode === 'vod' && sessionId && durationSec > 0) void seekTo(positionSec());
  else resyncForward(tMs);
}

function startRenderLoop(): void {
  cancelAnimationFrame(rafId);
  const controller = videoAbort;
  const draw = () => {
    if (!ui || !controller || controller !== videoAbort || controller.signal.aborted) return;
    const wall = performance.now();
    const gap = lastDrawWall ? wall - lastDrawWall : 0;
    maxDrawGapMs = Math.max(maxDrawGapMs, gap);
    lastDrawWall = wall;
    if (gap > SUSPEND_GAP_MS && !audioGate) hardResync();
    if (controller !== videoAbort) return;

    const tMs = audioClockSec() * 1000;

    // Takılma bekçisi: ses çalarken VE görüntü saatin gerisindeyken elimizdeki en
    // yeni içerik ilerlemeli. Donarsa ses yalnız akar — yeniden çapala. "Geride"
    // koşulu şart: sağlıklı dolu tamponda okuma durur ve en yeni pts saatin
    // İLERİSİNDE dururken ilerlemeyi kesmesi normaldir.
    if (!audioGate && !audioIsPaused() && basePtsUs !== null) {
      const newest = frameQueue.length
        ? frameQueue[frameQueue.length - 1].pts
        : (pendingChunks.length ? (pendingChunks[pendingChunks.length - 1].timestamp - basePtsUs) / 1000 : lastProgressPts);
      if (newest > lastProgressPts + 1) { lastProgressPts = newest; lastProgressWall = wall; staleStalls = 0; }
      // Akış tamamlandıysa yeni içerik gelmeyecektir; bu takılma değil, sondur.
      else if (!netVideoDone && newest < tMs && lastProgressWall && wall - lastProgressWall > STALL_MS) {
        lastProgressWall = wall;
        stallCount += 1;
        track('stall', { ...playbackRef(), value: Math.round(wall - (stallStartedAt || wall - STALL_MS)), data: { mode: mode ?? '' } });
        stallStartedAt = 0;
        // Ölçüm hardResync'ten ÖNCE gitmeli: yeniden çapalama akışı ve
        // sayaçlarını sıfırlayabilir, takılmanın nedeni o sayaçlarda.
        void beat();
        if (mode === 'vod' && durationSec > 0) {
          hardResync();
        } else {
          // Canlıda (ve süresi bilinmeyen içerikte) seek yok: hata vermeden asılı
          // kalan bir bağlantı hiçbir zaman toparlanmıyordu. Üç ardışık takılmada
          // (yastık bittikten sonra ~7,5 sn hiç veri yok) oturum yeniden kurulur;
          // deneme hakkı bittiyse eski davranış (elde olana atla) sürer.
          staleStalls += 1;
          if (staleStalls >= 3 && tryAutoRestart()) return;
          hardResync();
        }
      }
    }
    if (controller !== videoAbort) return;
    if (audioGate || audioIsPaused() || document.hidden) netSteady = false;
    else netMinLeadMs = Math.min(netMinLeadMs, leadMs());

    let index = -1;
    for (let i = 0; i < frameQueue.length; i++) {
      if (frameQueue[i].pts <= tMs) index = i; else break;
    }
    if (index >= 0) {
      const pts = frameQueue[index].pts;
      const fresh = lastDrawPts !== pts;
      if (fresh) drawnFrames += 1;
      lastDrawPts = pts;
      const frame = frameQueue[index].frame;
      const w = frame.displayWidth;
      const h = frame.displayHeight;
      let resized = false;
      if (ui.canvas.width !== w || ui.canvas.height !== h) {
        ui.canvas.width = w;
        ui.canvas.height = h;
        resized = true; // boyut değişimi tuvali siler
      }
      // Aynı kare yeniden çizilmez: 25 fps içerik 60 Hz ekranda saniyede 60 kez
      // (duraklatılmışken de) tuvale kopyalanıyordu. Tuval silindiyse (boyut
      // değişimi, seek, yeniden başlatma) kare değişmese de çizilir.
      if (fresh || resized || forcePaint) {
        ui.ctx.drawImage(frame, 0, 0, w, h);
        forcePaint = false;
      }
      for (let i = 0; i < index; i++) frameQueue.shift()!.frame.close();
      // İlk kare ekranda — ses artık başlayabilir.
      if (audioGate) releaseAudioGate();
    }
    lastDrawLagMs = lastDrawPts === null ? 0 : lastDrawPts - tMs;

    if (mode === 'vod' && durationSec > 0) {
      if (scrubbing) {
        // Sürükleme/atlama çubuğu ve etiketi kendisi yazıyor; bittiğinde yeniden boya.
        lastSeekPaint = -1;
        lastTimePaint = -1;
      } else {
        // DOM'a yalnız değer değişince yazılır (eskiden her karede üç yazım).
        const position = positionSec();
        const mark = Math.min(1000, Math.max(0, Math.round((position / durationSec) * 1000)));
        if (mark !== lastSeekPaint) {
          lastSeekPaint = mark;
          ui.seek.value = String(mark);
          paintRange(ui.seek);
        }
        const second = Math.round(position);
        if (second !== lastTimePaint) {
          lastTimePaint = second;
          ui.timeNow.textContent = formatDuration(position);
        }
      }
    }

    resyncIfBehind();
    pumpDecoder();
    pumpAudio();
    checkEnded();
    updateStats();
    rafId = requestAnimationFrame(draw);
  };
  draw();
}

function teardownVideo(): void {
  audioGate = false;
  netSteady = false;
  lastDrawWall = 0;
  lastDrawPts = null;
  maxDrawGapMs = 0;
  lastProgressWall = 0;
  lastProgressPts = -1;
  staleStalls = 0;
  streamLost = false;
  userPausedAt = 0;
  forcePaint = true;
  lastSeekPaint = -1;
  lastTimePaint = -1;
  if (videoAbort) { try { videoAbort.abort(); } catch { /* iptal */ } videoAbort = null; }
  cancelAnimationFrame(rafId);
  for (const item of frameQueue) { try { item.frame.close(); } catch { /* kapalı */ } }
  frameQueue = [];
  pendingChunks = [];
  basePtsUs = null;
  if (videoDecoder) { try { videoDecoder.close(); } catch { /* kapalı */ } videoDecoder = null; }
  if (videoMp4) { try { videoMp4.stop(); } catch { /* kapalı */ } videoMp4 = null; }
  teardownAudio();
}

// ================================ Oturum =====================================

export async function openPlayback(next: PlaybackTarget, close: () => void): Promise<void> {
  if (!('VideoDecoder' in window) || !('AudioDecoder' in window)) {
    toast(t('player.noWebCodecs'), 'error', 7000);
    return;
  }
  target = next;
  onClose = close;
  closing = false;
  if (!ui) ui = buildUi();
  // Odak oynatıcıya taşınır. Arkadaki kartta kalırsa Space/Enter o kartı yeniden
  // "oynatıyor" (oturumu baştan kuruyor), Esc de oynatıcıya hiç ulaşmıyordu.
  if (!ui.root.isConnected) {
    const active = document.activeElement;
    lastFocus = active instanceof HTMLElement && active !== document.body ? active : null;
  }
  document.body.appendChild(ui.root);
  try { document.getElementById('app')?.setAttribute('inert', ''); } catch { /* inert yok */ }
  try { ui.root.focus({ preventScroll: true }); } catch { /* odaklanamıyor */ }
  // Tarayıcının Geri tuşu (telefonda geri hareketi) oynatıcıyı kapatsın.
  if (!historyHeld) {
    historyHeld = true;
    pushOverlay(() => { historyHeld = false; closePlayer(); });
  }
  ui.root.classList.remove('idle');
  ui.title.textContent = next.title;
  ui.subtitle.textContent = next.subtitle ?? '';
  ui.veil.hidden = false;
  ui.veilText.textContent = t('player.connecting');
  ui.overlay.hidden = true;
  mount(ui.overlay, '');
  ui.channelBtn.hidden = !next.channelList?.length;
  resetIdle();
  await start(next, false);
}

async function start(next: PlaybackTarget, takeover: boolean): Promise<void> {
  // Nesil numarası, öncekini kapattıktan SONRA alınır: stopSession'ın kendisi
  // nesli artırıyor, önce alınsaydı taze oturum daha doğar doğmaz "eskimiş"
  // sayılıp silinirdi.
  stopSession(false);
  const requestGeneration = ++generation;
  // Kanal/kalite değişimi ve yeniden başlatma da buradan geçer: perde açılmazsa
  // ekranda saniyelerce eski yayının donuk karesi kalıyordu.
  if (ui) {
    ui.overlay.hidden = true;
    mount(ui.overlay, '');
    showVeil(t('player.connecting'));
    clearCanvas();
  }
  try {
    const hevc = await clientHevc();
    const payload = await api.post<PlaybackStart>('/api/playback/start', {
      source: next.source,
      quality: state.quality,
      takeover,
      startAt: next.startAt,
      hevc,
    }, undefined, START_TIMEOUT_MS);
    if (requestGeneration !== generation) {
      void api.del(`/api/playback/${payload.sessionId}`).catch(() => {});
      return;
    }
    sessionId = payload.sessionId;
    streamIsHevc = (payload.meta as Record<string, unknown> | undefined)?.videoCodec === 'hevc';
    pendingSeekSec = null;
    mode = payload.mode;
    itemKey = payload.itemKey;
    stallCount = 0;
    pauseCount = 0;
    stallStartedAt = 0;
    netStallsSent = 0;
    netBeatFails = 0;
    netRttMs = 0;
    netMinLeadMs = Infinity;
    durationSec = payload.durationSec ?? 0;
    baseOffsetSec = payload.startPositionSec ?? 0;
    updateQuota(payload.quota);
    paintQuota();
    if (ui) {
      ui.title.textContent = payload.title || next.title;
      ui.seek.disabled = mode !== 'vod' || durationSec <= 0;
      ui.timeEnd.textContent = mode === 'live' ? t('player.live') : durationSec > 0 ? formatDuration(durationSec) : '--:--';
      ui.timeNow.textContent = formatDuration(baseOffsetSec);
      ui.qualityBtn.hidden = next.source.type !== 'youtube' && next.source.type !== 'tv';
      ui.veilText.textContent = t('player.preparing');
    }
    directMedia = payload.media ?? null;
    startAudio(mediaUrl(payload.sessionId, 'audio'));
    void streamVideo(mediaUrl(payload.sessionId, 'video'));
    startRenderLoop();
    startHeartbeat();
  } catch (err) {
    if (requestGeneration !== generation) return;
    await handleStartError(err, next);
  }
}

async function handleStartError(err: unknown, next: PlaybackTarget): Promise<void> {
  if (!(err instanceof ApiError)) {
    showFatal(t('player.openFail'));
    return;
  }
  // Oturum düştü: yönlendirici girişe götürür, oynatıcı o olayla kapanır.
  if (err.status === 401) return;
  // Sunucuya ulaşılamadı: geçici olabilir (tünel, hat değişimi). Yalnız "geri"
  // sunmak yerine aynı yerden yeniden denemeye izin ver.
  if (err.code === 'network') { showLost(next); return; }
  if (err.code === 'stream_active') {
    const active = (err.detail.activeStream ?? {}) as { title?: string; deviceName?: string };
    const ok = await confirmDialog({
      title: t('player.activeTitle'),
      body: t('player.activeBody', {
        title: active.title ?? t('player.activeDefaultTitle'),
        device: active.deviceName ?? t('player.activeDefaultDevice'),
      }),
      confirmLabel: t('player.activeConfirm'),
    });
    if (!ok) { closePlayer(); return; }
    // Devir: eski yayın GERÇEKTEN durmadan yenisi açılmaz (sunucu bekletir).
    await start(next, true);
    return;
  }
  if (err.code === 'quota_exhausted') { showQuotaExhausted(); return; }
  if (err.code === 'iptv_requires_plus' || err.code === 'not_entitled') { showUpgrade(err.message); return; }
  if (err.code === 'server_busy') {
    showFatal(t('player.busy'));
    return;
  }
  // Sağlayıcı bizi sınırladıysa bu geçicidir ve kullanıcının yapabileceği tek
  // şey beklemek; "beklenmeyen hata" demek yanlış yönlendiriyordu.
  if (err.code === 'provider_blocked' || err.code === 'provider_unavailable') {
    showFatal(err.message);
    return;
  }
  showFatal(err.message || t('player.openFail'));
}

/** "Kaldığınız yerden" kartının görsel ve kanal bilgisi: ilerleme kaydıyla
 *  birlikte gider; gönderilmezse sunucu eski kaydı boş nesneyle eziyordu. */
function progressMeta(): Record<string, unknown> {
  const source = target?.source;
  if (!source) return {};
  if (source.type === 'youtube') {
    return { videoId: source.videoId, channel: source.channel ?? '', durationSec: source.durationSec ?? null, live: !!source.live };
  }
  if (source.type === 'iptv') return { itemId: source.itemId };
  if (source.type === 'tv') return { channelId: source.channelId };
  return {};
}

/** Son kalp atışından bu yana ölçülenler; okunduğunda pencere sıfırlanır. */
function netSample(): Record<string, unknown> {
  const conn = (navigator as any).connection;
  const sample = {
    epoch: netEpoch,
    ageMs: Math.round(performance.now() - netStreamAt),
    steady: netSteady && !netVideoDone,
    minLeadMs: Number.isFinite(netMinLeadMs) ? Math.max(0, Math.round(netMinLeadMs)) : 600_000,
    stalls: stallCount - netStallsSent,
    // Gizli sekmede çizim döngüsü durur ve perde kalkamaz; o süre bekleme sayılmaz.
    gateMs: audioGate && !document.hidden ? Math.round(performance.now() - netGateAt) : 0,
    rxBytes: netRxBytes,
    contentSec: Math.round(netContentSec * 10) / 10,
    fails: netBeatFails,
    rttMs: Math.round(netRttMs),
    downlink: Number(conn?.downlink) || 0,
    effectiveType: String(conn?.effectiveType ?? ''),
  };
  netSteady = true;
  netMinLeadMs = Infinity;
  netStallsSent = stallCount;
  return sample;
}

async function beat(): Promise<void> {
  if (!sessionId) return;
  const id = sessionId;
  const sentAt = performance.now();
  try {
    const result = await api.post<{ ended: boolean; stopping?: boolean; reason?: string; quota?: any; net?: string | null }>(
      `/api/playback/${id}/heartbeat`,
      {
        positionSec: positionSec(),
        durationSec,
        itemKey,
        mode,
        paused: audioIsPaused(),
        counters: { stalls: stallCount, resyncs, pauses: pauseCount },
        meta: progressMeta(),
        net: netSample(),
      },
      undefined,
      BEAT_TIMEOUT_MS,
    );
    netRttMs = performance.now() - sentAt;
    netBeatFails = 0;
    updateQuota(result.quota);
    paintQuota();
    if (sessionId !== id) return;
    if (result.net === 'client') showNetHint();
    if (result.stopping) handleServerStop(result.reason ?? 'stopped');
  } catch (err) {
    if (!(err instanceof ApiError)) return;
    if (err.status === 410) {
      if (sessionId === id) handleServerStop(String((err.detail as any)?.reason ?? 'ended'));
    } else if (err.status === 404) {
      // Sunucu oturumu hiç tanımıyor: kirası dolmuş gibi ele alınır.
      if (sessionId === id) handleServerStop('not_found');
    } else if (err.code === 'network') {
      // Sunucuya hiç ulaşamayan iki atış: sunucunun teşhisini beklemeden uyar.
      netBeatFails += 1;
      if (netBeatFails >= 2) showNetHint();
    }
  }
}

function startHeartbeat(): void {
  stopHeartbeat();
  heartbeatTimer = window.setInterval(() => void beat(), 8000);
}

/**
 * "Bağlantınız yavaş" uyarısı. Oynatıcının İÇİNDE durur: tam ekranda sayfanın
 * bildirim yığını görünmez. Kısa süre kalır, dakikada birden sık çıkmaz.
 */
function showNetHint(): void {
  if (!ui || !sessionId) return;
  const now = performance.now();
  if (netHintAt && now - netHintAt < NET_HINT_GAP_MS) return;
  netHintAt = now;
  // Kalite seçilebiliyorsa ve düşürülecek bir basamak varsa bunu da söyle.
  const canLower = !ui.qualityBtn.hidden && state.quality > 360;
  ui.netHint.textContent = t(canLower ? 'player.netSlowQuality' : 'player.netSlow');
  ui.netHint.hidden = false;
  if (netHintTimer) clearTimeout(netHintTimer);
  netHintTimer = window.setTimeout(hideNetHint, NET_HINT_SHOW_MS);
}

function hideNetHint(): void {
  if (netHintTimer) { clearTimeout(netHintTimer); netHintTimer = null; }
  if (ui) ui.netHint.hidden = true;
}

function stopHeartbeat(): void {
  if (heartbeatTimer) { clearInterval(heartbeatTimer); heartbeatTimer = null; }
}

function handleServerStop(reason: string): void {
  // Konum stopSession'dan ÖNCE okunmalı: o, modu ve saati sıfırlıyor.
  if (reason === 'source_ended') {
    if (mode === 'vod') {
      // Bitişe burada karar verilmez. Eskiden verilirdi: sunucu "bitti" dediği an
      // öneri ekranı açılıyor (sonra: kaldığı yerden yeni oturum başlatılıyor)
      // ve izleyici videonun ortasında kesiliyordu. Gerçek bitişi tamponun
      // boşalması söyler (checkEnded); erken kopmuşsa devam kararı da orada.
      serverEnded = true;
      stopHeartbeat();
      return;
    }
    // Ses akışının kapanması (checkEnded) aynı bitişi zaten işlediyse ikinci kez başlatma.
    if (audioEndedFired) return;
    audioEndedFired = true;
    finishOrResume();
    return;
  }
  // Oturum sunucuda sessizce kapandı: kira doldu (sekme arka plandaydı, telefon
  // kilitlendi, ağ bir süre yoktu) ya da duraklatma zaman aşımına uğradı. Bu,
  // izleyicinin yaptığı bir şey değil; "Yayın durduruldu" demek yerine kaldığı
  // yerden yeni oturum açılır.
  if (reason === 'lease_expired' || reason === 'paused_timeout' || reason === 'not_found') {
    const waiting = !audioWantsPlay || document.hidden;
    if (waiting || (mode === 'vod' && durationSec > 0)) {
      // Duraklatılmış/gizliyken kendiliğinden oynatmaya başlama: oturumun
      // kapandığını not et, devam edildiğinde (resumePlayback/seekTo) yeniden aç.
      // Oynayan VOD'da tampon boşalınca checkEnded aynı yoldan devam ettirir.
      serverEnded = true;
      stopHeartbeat();
      return;
    }
    autoRestart();
    return;
  }
  stopSession(false);
  if (reason === 'quota_exhausted') { showQuotaExhausted(); return; }
  if (reason === 'taken_over') {
    showBlocking(t('player.takenTitle'), t('player.takenBody'));
    return;
  }
  if (reason === 'entitlement_lost') {
    showUpgrade(t('player.entitlementLost'));
    return;
  }
  if (reason === 'preempted') {
    showBlocking(t('player.preemptedTitle'), t('player.preemptedBody'));
    return;
  }
  showBlocking(t('player.stoppedTitle'), t('player.stoppedBody'));
}

// ================================ Seek =======================================

async function seekTo(seconds: number): Promise<void> {
  if (mode !== 'vod' || !sessionId) return;
  // Sunucudaki oturum kapandıysa ileri alınacak bir ffmpeg yok: o konumdan
  // yeni oturum açılır.
  const want = Math.max(0, Number.isFinite(seconds) ? seconds : 0);
  if (serverEnded && target) {
    await start({ ...target, startAt: want }, false);
    return;
  }
  const id = sessionId;
  const requestGeneration = ++generation;
  const current = () => sessionId === id && requestGeneration === generation;
  // Hedef, akış yıkılmadan ÖNCE kaydedilir: istek sürerken (ve başarısız olursa)
  // konum soran herkes bu değeri görür, eski tabanı değil.
  pendingSeekSec = want;
  seekIntent = null;
  teardownVideo();
  // Perde isteği beklemeden açılır: yanıt gelene dek ekranda göstergesiz donuk
  // kare kalıyordu.
  showVeil(t('player.seeking'));
  clearCanvas();
  let landed = want;
  for (let attempt = 0; ; attempt++) {
    try {
      const result = await api.post<{ t: number }>(`/api/playback/${id}/seek`, safeSeek ? { t: want, safe: true } : { t: want }, undefined, SEEK_TIMEOUT_MS);
      if (!current()) return;
      landed = Number.isFinite(result.t) ? result.t : want;
      break;
    } catch (err) {
      if (!current()) return;
      const status = err instanceof ApiError ? err.status : 0;
      // Oturum düştü: yönlendirici girişe götürür, oynatıcı o olayla kapanır.
      if (status === 401) return;
      // Sunucuda oturum yok (kirası doldu, duraklatma zaman aşımı): aynı konumdan
      // sessizce yeni oturum.
      if (status === 404 || status === 410) {
        const next = target ? { ...target, startAt: want } : null;
        if (!next) { closePlayer(); return; }
        if (!restartBudget()) { showLost(next); return; }
        await start(next, false);
        return;
      }
      // Ağ hatası, 502 (konum doğrulanamadı), bu nesilde gelen 409: sınırlı sayıda
      // yeniden dene. Eskiden tek hata oynatıcıyı donuk karede bırakıyordu.
      if (attempt >= SEEK_RETRY_MS.length) {
        showLost(target ? { ...target, startAt: want } : null);
        return;
      }
      if (ui) ui.veilText.textContent = t('player.reconnecting');
      await sleep(SEEK_RETRY_MS[attempt]);
      if (!current()) return;
    }
  }
  baseOffsetSec = landed;
  pendingSeekSec = null;
  if (!ui) return;
  showVeil(t('player.seeking'));
  startAudio(mediaUrl(id, 'audio', true));
  // Seek beklenirken duraklat'a basıldıysa niyet korunur (startAudio "oynat"a çeker).
  if (seekIntent === false) audioWantsPlay = false;
  seekIntent = null;
  void streamVideo(mediaUrl(id, 'video'), true);
  startRenderLoop();
}

/** Perdeyi (halka + metin) aç. */
function showVeil(text: string): void {
  if (!ui) return;
  ui.veil.hidden = false;
  ui.veilText.textContent = text;
}

/** Tuvali sil; sıradaki kare, öncekiyle aynı zaman damgasını taşısa da çizilsin. */
function clearCanvas(): void {
  if (!ui) return;
  try { ui.ctx.clearRect(0, 0, ui.canvas.width, ui.canvas.height); } catch { /* bağlam yok */ }
  forcePaint = true;
}

/**
 * Kenara dokunma ile atlama. Bir seek sunucuda ffmpeg'i yeniden doğurup anahtar
 * kare sondası yapıyor (~2 sn), bu yüzden ard arda dokunuşlar TEK sıçramada
 * birleştirilir — kumandanın atlama düğmesi gibi.
 */
function skipBy(delta: number): void {
  if (mode !== 'vod' || !sessionId || durationSec <= 0) return;
  if (!pendingSkip) skipBase = skipCommitted !== null ? skipCommitted : positionSec();
  pendingSkip += delta;
  const targetSec = Math.min(Math.max(skipBase + pendingSkip, 0), Math.max(0, durationSec - 1));
  scrubbing = true;
  if (ui) {
    ui.seek.value = String(Math.round((targetSec / durationSec) * 1000));
    paintRange(ui.seek);
    ui.timeNow.textContent = formatDuration(targetSec);
    ui.skipHint.className = `skip-hint ${pendingSkip < 0 ? 'back' : 'fwd'}`;
    ui.skipHint.hidden = false;
    mount(ui.skipHint,
      el('div', {}, (pendingSkip < 0 ? '−' : '+') + t('player.skipSec', { n: Math.abs(Math.round(pendingSkip)) })),
      el('div', { class: 'target' }, formatDuration(targetSec)),
    );
  }
  resetIdle();
  if (skipTimer) clearTimeout(skipTimer);
  skipTimer = window.setTimeout(async () => {
    track('skip', { ...playbackRef(), value: Math.round(pendingSkip), data: { to: Math.round(targetSec) } });
    pendingSkip = 0;
    skipTimer = null;
    const mine = ++skipSeq;
    skipCommitted = targetSec;
    await seekTo(targetSec);
    // Bu atlama beklerken yenisi başladıysa (ya da oturum kapandıysa) onun
    // durumuna dokunma: eskiden burada silinen hedef, üçüncü dokunuşu başa atıyordu.
    if (mine !== skipSeq) return;
    skipCommitted = null;
    if (!pendingSkip && ui) { ui.skipHint.hidden = true; scrubbing = false; }
  }, SKIP_COALESCE_MS);
}

// ================================ Bitiş ======================================

function checkEnded(): void {
  if (mode !== 'vod' || audioEndedFired) return;
  if (!audioFetchDone || audioPaused) return;
  // Sunucu oturumu kapatmış ve hiç görüntü gelmemişse perdeyi kaldıracak kare
  // de gelmeyecektir; sonsuza dek "hazırlanıyor" göstermek yerine bitişe geç.
  const videoDry = videoFetchOver && !frameQueue.length && !pendingChunks.length && !(videoDecoder?.decodeQueueSize);
  const deadGate = serverEnded && audioGate && videoDry;
  if (!deadGate) {
    if (pendingAudio.length > 0 || audioSources.length > 0) return;
    // Ses hiç başlamadıysa bitiş yalnız sunucu kapattıysa ve çalacak şey kalmadıysa sayılır.
    if (!audioClockRunning && !(serverEnded && !audioGate)) return;
  }
  audioEndedFired = true;
  finishOrResume();
}

// Kaynak (çoğunlukla YouTube'un googlevideo sunucusu) akışı videonun ortasında
// kesince sunucu "içerik bitti" diyordu ve izleyici öneri ekranını görüyordu.
// Süre biliniyorsa ve sona gelinmediyse kaldığı yerden yeni oturum açılır.
// Sınırlı: aynı içerikte 2 dakika içinde en fazla 3 deneme, sonra gerçek bitiş.
const RESUME_WINDOW_MS = 120_000;
const RESUME_MAX = 3;
let resumeLog: { key: string | null; times: number[] } = { key: null, times: [] };

/** Otomatik yeniden başlatma hakkı: aynı içerikte 2 dakikada en çok 3. */
function restartBudget(): boolean {
  const now = Date.now();
  if (resumeLog.key !== itemKey) resumeLog = { key: itemKey, times: [] };
  resumeLog.times = resumeLog.times.filter((at) => now - at < RESUME_WINDOW_MS);
  if (resumeLog.times.length >= RESUME_MAX) return false;
  resumeLog.times.push(now);
  return true;
}

/** Şu anki içerik, kaldığı yerden (VOD) ya da canlı ucundan yeniden açılacak hedef. */
function resumeTarget(): PlaybackTarget | null {
  if (!target) return null;
  const position = positionSec();
  // Henüz hiç oynamadıysa konum gönderme: sunucu kayıtlı "kaldığın yer"i kullansın.
  const startAt = mode === 'vod' && position > 1 ? position : undefined;
  return { ...target, startAt };
}

/** Hak varsa oturumu sessizce yeniden kurar; yoksa hiçbir şey yapmaz. */
function tryAutoRestart(): boolean {
  if (!sessionId || closing) return false;
  const next = resumeTarget();
  if (!next || !restartBudget()) return false;
  void start(next, false);
  return true;
}

/** Oturumu yeniden kur; hak bittiyse "Tekrar deneyin" düğmeli ekranı göster. */
function autoRestart(): void {
  if (closing) return;
  const next = resumeTarget();
  if (!next) { closePlayer(); return; }
  if (!restartBudget()) { showLost(next); return; }
  void start(next, false);
}

/**
 * Akış bağlantısı koptu (fetch hata verdi) ya da ses akışı hiç alınamadı.
 * Süresi bilinen VOD'da oturum yaşıyorsa aynı konuma seek yeterlidir (seek kendi
 * içinde yeniden dener; oturum gitmişse yenisini açar). Canlıda ve süresi
 * bilinmeyen içerikte seek yoktur: oturum yeniden kurulur.
 */
function onStreamLost(): void {
  if (!sessionId || closing) return;
  // Duraklatılmış ya da gizli sekmede bağlantının kopması olağandır (okunmayan
  // yanıtı vekil sunucu bir süre sonra kapatır). Kendiliğinden oynatmaya başlama;
  // not al, devam edildiğinde akış yeniden kurulur (resumePlayback).
  if (!audioWantsPlay || document.hidden) { streamLost = true; return; }
  if (mode === 'vod' && durationSec > 0 && !serverEnded) {
    if (!restartBudget()) { showLost(resumeTarget()); return; }
    void seekTo(positionSec());
    return;
  }
  autoRestart();
}

/** Bağlantı kurulamıyor: oturumu bırak, "Tekrar deneyin" + "Geri dön" sun. */
function showLost(next: PlaybackTarget | null): void {
  track('player_error', { ...playbackRef(), data: { message: 'connection_lost' } });
  stopSession(false);
  if (!ui) return;
  ui.veil.hidden = true;
  ui.overlay.hidden = false;
  mount(ui.overlay, el('div', { class: 'inner' },
    stateBlock(next
      ? {
          title: t('player.lostTitle'),
          body: t('player.lostBody'),
          action: { label: t('common.retry'), onClick: () => { if (!closing) void start(next, false); } },
          secondary: { label: t('common.back'), onClick: closePlayer },
        }
      : {
          title: t('player.lostTitle'),
          body: t('player.lostBody'),
          action: { label: t('common.back'), onClick: closePlayer },
        }),
  ));
}

function finishOrResume(): void {
  const position = positionSec();
  // Canlı yayın "bitmez": kaynak koptuysa (sunucudaki ffmpeg çıktı) yeniden
  // bağlanılır. Eskiden oynatıcı mesajsız kapanıyor, YouTube canlısında yayın
  // bitmiş gibi öneri ekranı açılıyordu.
  if (mode === 'live') {
    // Duraklatılmış/gizliyken kendiliğinden oynatmaya başlama; devam edilince açılır.
    if (!audioWantsPlay || document.hidden) { serverEnded = true; stopHeartbeat(); return; }
    autoRestart();
    return;
  }
  if (mode === 'vod' && target && isPrematureEnd(position, durationSec)) {
    if (restartBudget()) {
      void start({ ...target, startAt: position }, false);
      return;
    }
  }
  onStreamFinished();
}

function onStreamFinished(): void {
  const finished = target;
  const wasLive = mode === 'live';
  stopSession(true);
  if (!finished) { closePlayer(); return; }
  // Öneri ekranı yalnız biten VOD içindir; canlıda asla açılmaz.
  if (finished.onRelated && ui && !wasLive) {
    track('related_open', { kind: finished.source.type, itemKey, title: finished.title });
    ui.overlay.hidden = false;
    const host = el('div', { class: 'inner' });
    mount(ui.overlay, host);
    finished.onRelated(host, closePlayer);
    return;
  }
  closePlayer();
}

// ============================ Durum ekranları ================================

function showBlocking(title: string, body: string, extra?: HTMLElement): void {
  if (!ui) return;
  ui.veil.hidden = true;
  ui.overlay.hidden = false;
  mount(ui.overlay, el('div', { class: 'inner' },
    stateBlock({ title, body, action: { label: t('common.back'), onClick: closePlayer } }),
    extra ?? null,
  ));
}

function showFatal(message: string): void {
  track('player_error', { ...playbackRef(), data: { message: message.slice(0, 200) } });
  stopSession(false);
  showBlocking(t('player.cantOpen'), message);
}

function showQuotaExhausted(): void {
  if (!ui) return;
  ui.veil.hidden = true;
  ui.overlay.hidden = false;
  mount(ui.overlay, el('div', { class: 'inner' },
    stateBlock({
      title: t('player.quotaTitle'),
      body: t('player.quotaBody'),
      action: { label: t('common.viewPlans'), onClick: () => { closePlayer(); afterHistorySettled(() => location.assign('/plans')); } },
      secondary: { label: t('common.back'), onClick: closePlayer },
    }),
  ));
}

function showUpgrade(message: string): void {
  if (!ui) return;
  ui.veil.hidden = true;
  ui.overlay.hidden = false;
  mount(ui.overlay, el('div', { class: 'inner' },
    stateBlock({
      title: t('player.plusTitle'),
      body: message,
      action: { label: t('player.goPlus'), onClick: () => { closePlayer(); afterHistorySettled(() => location.assign('/plans')); } },
      secondary: { label: t('common.back'), onClick: closePlayer },
    }),
  ));
}

// ============================== Kapanış ======================================

function stopSession(keepUi: boolean): void {
  generation += 1;
  audioWantsPlay = false;
  serverEnded = false;
  safeSeek = false;
  stopHeartbeat();
  hideNetHint();
  const id = sessionId;
  // Konum, seek hedefi dahil, her şey sıfırlanmadan ÖNCE okunur.
  const position = positionSec();
  pendingSeekSec = null;
  seekIntent = null;
  // Bekleyen atlama yeni oturuma taşmasın (eski içeriğin hedefine sarıyordu).
  if (skipTimer) { clearTimeout(skipTimer); skipTimer = null; }
  pendingSkip = 0;
  skipCommitted = null;
  skipSeq += 1;
  scrubbing = false;
  if (ui) ui.skipHint.hidden = true;
  releaseWakeLock();
  teardownVideo();
  if (id) {
    void api.del(`/api/playback/${id}`, {
      positionSec: position,
      durationSec,
      itemKey,
      mode,
      meta: progressMeta(),
    }).catch(() => {});
  }
  sessionId = null;
  if (!keepUi) mode = null;
}

export function closePlayer(): void {
  if (closing) return;
  closing = true;
  stopSession(false);
  closeMenus();
  if (ui) {
    ui.root.remove();
    ui.skipHint.hidden = true;
    ui.overlay.hidden = true;
  }
  try { document.getElementById('app')?.removeAttribute('inert'); } catch { /* inert yok */ }
  // Ses bağlamı boşta çalışır kalmasın; bir sonraki oynatmada ensureAudioCtx sürdürür.
  try { void audioCtx?.suspend().catch(() => {}); } catch { /* kapalı */ }
  // Açılırken eklenen geçmiş girdisini geri al (Geri tuşuyla kapandıysa zaten gitti).
  if (historyHeld) { historyHeld = false; popOverlay(); }
  const focusBack = lastFocus;
  lastFocus = null;
  try { if (focusBack && focusBack.isConnected) focusBack.focus({ preventScroll: true }); } catch { /* odaklanamıyor */ }
  const back = onClose;
  onClose = null;
  target = null;
  if (back) back();
}

// ================================ Arayüz =====================================

let quotaWarned = false;
let rotateTimer: number | null = null;
const DRIVE_KEY = 'akis.surus-uyarisi';

/**
 * Araç ekranında sürüş uyarısı. Oturumda bir kez gösterilir.
 *
 * Aracın hareket hâlinde olup olmadığını tarayıcıdan öğrenemiyoruz — böyle bir
 * bilgi web sayfasına verilmiyor — bu yüzden oynatmayı otomatik durduramayız.
 * Yapabileceğimiz şey söylemek. Engellemiyor, kapatılabiliyor.
 */
function maybeDriveWarning(): void {
  if (!isCar()) return;
  try {
    if (sessionStorage.getItem(DRIVE_KEY) === '1') return;
    sessionStorage.setItem(DRIVE_KEY, '1');
  } catch { /* depolama kapalı: yine de bir kez göster */ }
  toast(t('player.drive'), 'info', 9000);
}

/** Telefon dikeyken kısa bir çevirme ipucu. Tam ekranda ya da yatayda çıkmaz. */
function maybeRotateHint(): void {
  if (!ui) return;
  if (rotateTimer) { clearTimeout(rotateTimer); rotateTimer = null; }
  const portrait = window.innerHeight > window.innerWidth;
  if (!isPhone() || !portrait) { ui.rotate.hidden = true; return; }
  ui.rotate.hidden = false;
  rotateTimer = window.setTimeout(() => { if (ui) ui.rotate.hidden = true; }, 4000);
}

/**
 * Kalan ücretsiz süreyi oynatıcıda göster ve bitmeden önce uyar.
 * Eskiden sınır hiçbir uyarı olmadan doluyordu: yayın duruyor, kullanıcı
 * nedenini bilmiyordu.
 */
function paintQuota(): void {
  if (!ui) return;
  const quota = state.session?.quota;
  // Hazır kanallar kotadan düşmez: sayaç orada yanıltıcı olurdu.
  if (!quota || quota.limitSeconds === null || target?.source.type === 'tv') { ui.quota.hidden = true; return; }
  const left = Math.max(0, Math.round(quota.remainingSeconds ?? 0));
  ui.quota.hidden = false;
  ui.quota.className = 'player-quota' + (left <= 120 ? ' low' : '');
  ui.quota.textContent = formatQuotaLeft(left);
  if (left > 0 && left <= 60 && !quotaWarned) {
    quotaWarned = true;
    toast(t('player.quotaSoon'), 'info', 8000);
  }
  if (left > 120) quotaWarned = false;
}

function setPlayIcon(playing: boolean): void {
  if (!ui) return;
  ui.playBtn.replaceChildren(playing ? iconEl('pause') : iconEl('play'));
  ui.playBtn.setAttribute('aria-label', playing ? t('player.ctl.pause') : t('player.ctl.play'));
}

function resetIdle(): void {
  if (!ui) return;
  ui.root.classList.remove('idle');
  if (idleTimer) clearTimeout(idleTimer);
  idleTimer = window.setTimeout(() => ui?.root.classList.add('idle'), 4000);
}

function closeMenus(): void {
  if (ui) mount(ui.menuHost, '');
}

function openMenu(items: Array<{ label: string; active?: boolean; onSelect: () => void }>): void {
  if (!ui) return;
  const menu = el('div', { class: 'player-menu', role: 'menu' });
  for (const item of items) {
    const button = el('button', { type: 'button', class: item.active ? 'active' : '', role: 'menuitem' }, item.label);
    onTap(button, () => { closeMenus(); item.onSelect(); });
    menu.appendChild(button);
  }
  mount(ui.menuHost, menu);
  (menu.firstElementChild as HTMLElement | null)?.focus();
  resetIdle();
}

function paintRange(input: HTMLInputElement): void {
  const percent = (Number(input.value) - Number(input.min)) / (Number(input.max) - Number(input.min)) * 100;
  input.style.setProperty('--range-progress', `${Math.max(0, Math.min(100, percent))}%`);
}

function buildUi(): Ui {
  const canvas = el('canvas', { width: 1280, height: 720 });
  // Opak tuval: görüntünün arkasında bir şey yok, alfa karıştırması boşa maliyet.
  const ctx = (canvas.getContext('2d', { alpha: false }) ?? canvas.getContext('2d')) as CanvasRenderingContext2D;
  const veilText = el('p', {}, t('player.connecting'));
  const veil = el('div', { class: 'veil' }, el('div', { class: 'spinner' }), veilText);
  const title = el('div', { class: 'title' });
  const subtitle = el('div', { class: 'sub' });
  const stats = el('div', { class: 'stats-hud', hidden: true });
  const skipHint = el('div', { class: 'skip-hint', hidden: true });
  const overlay = el('div', { class: 'player-overlay', hidden: true });
  const menuHost = el('div', {});

  const backBtn = el('button', { class: 'ctl back', type: 'button', 'aria-label': t('player.ctl.back'), title: t('player.ctl.close') }, iconEl('arrowLeft'));
  const playBtn = el('button', {
    class: 'ctl play', type: 'button', 'aria-label': t('player.ctl.play'), title: t('player.ctl.playTitle'),
  }, iconEl('play'));
  const backSkip = el('button', {
    class: 'ctl skip', type: 'button', 'aria-label': t('player.ctl.back10'), title: t('player.ctl.back10Title'),
  }, iconEl('back10'));
  const fwdSkip = el('button', {
    class: 'ctl skip', type: 'button', 'aria-label': t('player.ctl.fwd10'), title: t('player.ctl.fwd10Title'),
  }, iconEl('fwd10'));
  const channelBtn = el('button', { class: 'ctl', type: 'button', 'aria-label': t('player.ctl.channels') }, iconEl('list'));
  const qualityBtn = el('button', { class: 'ctl wide', type: 'button', 'aria-label': t('player.ctl.quality') },
    el('span', {}, t('player.ctl.qualityLabel', { q: state.quality })), iconEl('chevronDown'));
  const fullBtn = el('button', { class: 'ctl', type: 'button', 'aria-label': t('player.ctl.fullscreen'), title: t('player.ctl.fullscreenTitle') }, iconEl('expand'));
  // Araçta kaydırıcı yok: 100×4 px'lik bir şeridi titreşimli ekranda tutmak
  // zor ve ses zaten aracın kendi kumandasında. Sessiz/aç yeter.
  const muted = audioVolume <= 0.001;
  const muteBtn = el('button', { class: 'ctl ctl-mute', type: 'button', 'aria-label': muted ? t('player.ctl.unmute') : t('player.ctl.mute') }, iconEl(muted ? 'volumeOff' : 'volume'));
  const volume = el('input', { type: 'range', min: '0', max: '1', step: '0.01', value: '1', 'aria-label': t('player.ctl.volume') });
  const seek = el('input', { type: 'range', min: '0', max: '1000', value: '0', 'aria-label': t('player.ctl.position') });
  volume.value = String(audioVolume);
  paintRange(volume);
  paintRange(seek);
  const timeNow = el('span', { class: 't' }, '0:00');
  const timeEnd = el('span', { class: 't right' }, t('player.live'));

  const controls = el('div', { class: 'player-controls' },
    el('div', { class: 'seek-row' }, timeNow, seek, timeEnd),
    el('div', { class: 'ctl-row' },
      el('div', { class: 'ctl-transport' }, playBtn, backSkip, fwdSkip,
        el('span', { class: 'ctl-vol' }, muteBtn, volume)),
      el('div', { class: 'ctl-options' }, channelBtn, qualityBtn, fullBtn),
    ),
  );

  // Kalan ücretsiz süre oynatıcıda da görünür: sınır tam burada doluyor ve
  // "neden durdu?" şaşkınlığı burada yaşanıyordu.
  const quotaTag = el('div', { class: 'player-quota', hidden: true });
  // Telefonda dikey tutulduğunda görüntü küçücük bir şeride sıkışıyor ama
  // hiçbir yerde "çevir" demiyorduk. Kısa süre görünür, sonra kaybolur.
  const rotate = el('div', { class: 'rotate-hint', hidden: true },
    iconEl('expand'), el('span', {}, t('player.rotate')));
  const netHint = el('div', { class: 'net-hint', hidden: true, role: 'status' });
  const top = el('div', { class: 'player-top' },
    backBtn,
    el('div', { class: 'titlebox' }, title, subtitle),
    el('span', { style: { flex: '1' } }),
    quotaTag,
  );
  const root = el('div', { class: 'player', tabindex: '0' }, canvas, veil, top, controls, rotate, netHint, skipHint, stats, overlay, menuHost);

  onTap(backBtn, () => closePlayer());
  onTap(playBtn, () => togglePlay());
  onTap(backSkip, () => skipBy(-SKIP_STEP));
  onTap(fwdSkip, () => skipBy(SKIP_STEP));
  onTap(fullBtn, () => toggleFullscreen(root));
  // Tanı göstergesi normalde kapalı; başlığa dokununca açılır.
  onTap(title, () => { statsOn = !statsOn; stats.hidden = !statsOn; statsAt = 0; resetIdle(); });

  onTap(qualityBtn, () => {
    openMenu([1080, 720, 480, 360].map((q) => ({
      label: `${q}p`,
      active: q === state.quality,
      onSelect: () => void changeQuality(q),
    })));
  });

  onTap(channelBtn, () => {
    const list = target?.channelList ?? [];
    openMenu(list.map((channel) => ({
      label: channel.name,
      active: JSON.stringify(channel.target.source) === JSON.stringify(target?.source),
      onSelect: () => {
        void start(channel.target, false);
        if (target) target.source = channel.target.source;
        if (ui) ui.title.textContent = channel.name;
      },
    })));
  });

  const applyVolume = (value: number) => {
    audioVolume = value;
    if (audioGain) audioGain.gain.value = audioVolume;
    volume.value = String(value);
    paintRange(volume);
    muteBtn.replaceChildren(audioVolume <= 0.001 ? iconEl('volumeOff') : iconEl('volume'));
    muteBtn.setAttribute('aria-label', audioVolume <= 0.001 ? t('player.ctl.unmute') : t('player.ctl.mute'));
    resetIdle();
  };
  let volumeBeforeMute = 1;
  onTap(muteBtn, () => {
    if (audioVolume > 0.001) { volumeBeforeMute = audioVolume; applyVolume(0); }
    else applyVolume(volumeBeforeMute || 1);
  });
  volume.addEventListener('input', () => applyVolume(Number(volume.value)));

  seek.addEventListener('input', () => {
    if (mode !== 'vod') return;
    scrubbing = true;
    paintRange(seek);
    // Sürüklerken hedef süre görünsün (etiket eskiden sürükleme başındaki anda kalıyordu).
    if (durationSec > 0) timeNow.textContent = formatDuration((Number(seek.value) / 1000) * durationSec);
    resetIdle();
  });
  seek.addEventListener('change', () => {
    if (mode !== 'vod' || durationSec <= 0) return;
    void seekTo((Number(seek.value) / 1000) * durationSec);
    scrubbing = false;
  });

  root.addEventListener('pointermove', resetIdle);
  root.addEventListener('pointerdown', (e) => {
    resetIdle();
    if (!(e.target as HTMLElement).closest('.player-menu')) closeMenus();
  });

  // Resmin sol/sağ üçte biri atlama alanı; orta bölge yalnız kontrolleri uyandırır.
  //
  // Bölge kararı pointerUP'taki hedefe göre verilemez: kontrol düğmeleri
  // basıldığında kendi ikonlarını değiştiriyor (oynat ↔ duraklat), dolayısıyla
  // pointerup anında olayın hedefi DOM'dan kopmuş oluyor ve `closest()` null
  // dönüyordu. Sonuç: duraklat düğmesine dokunmak aynı zamanda 10 saniye geri
  // alıyordu. Hedefi basma anında yakalıyoruz.
  let pressOrigin: HTMLElement | null = null;
  root.addEventListener('pointerdown', (e) => { pressOrigin = e.target as HTMLElement; }, true);
  onTap(root, (event) => {
    const origin = pressOrigin;
    pressOrigin = null;
    // Yalnızca RESMİN kendisine yapılan dokunuş atlama sayılır. "Kontrol
    // değilse atla" kuralı kırılgandı; burada olumlu koşul aranıyor.
    if (origin !== root && origin !== canvas) return;
    if (mode !== 'vod') return;
    const pointer = event as PointerEvent;
    const box = root.getBoundingClientRect();
    const x = (pointer.clientX - box.left) / box.width;
    if (x < SKIP_ZONE) skipBy(-SKIP_STEP);
    else if (x > 1 - SKIP_ZONE) skipBy(SKIP_STEP);
  });

  root.addEventListener('keydown', (e) => {
    resetIdle();
    const from = e.target as HTMLElement | null;
    // Onay kutusu açıksa tuşlar onundur (Esc'i kendi dinleyicisi işler).
    if (root.querySelector('.backdrop')) return;
    if (e.key === 'Escape') {
      // Önce açık menü kapanır; menü yoksa oynatıcı.
      if (menuHost.firstElementChild) {
        closeMenus();
        try { root.focus({ preventScroll: true }); } catch { /* odaklanamıyor */ }
      } else {
        closePlayer();
      }
      return;
    }
    // Odak bir düğmede/menü öğesindeyken Space ve Enter o öğenindir (onTap işler).
    // Burada da işlenince tek tuş iki eylem yapıyordu: oynat düğmesinde Space
    // duraklatıp hemen sürdürüyor, sessiz düğmesinde hem sesi hem oynatmayı çeviriyordu.
    const onControl = !!from?.closest?.('button, [role="menuitem"], a, select, textarea');
    if ((e.key === ' ' || e.key === 'Enter') && onControl) return;
    // Ses kaydırıcısında ok tuşları sesi ayarlar, atlamaz.
    if (from === volume && e.key.startsWith('Arrow')) return;
    if (e.key === ' ' || e.key === 'k') { e.preventDefault(); togglePlay(); }
    else if (e.key === 'ArrowLeft') { e.preventDefault(); skipBy(-SKIP_STEP); }
    else if (e.key === 'ArrowRight') { e.preventDefault(); skipBy(SKIP_STEP); }
    else if (e.key === 'f') toggleFullscreen(root);
  });

  // Sekme arkaya alınınca çizim döngüsü durur ama ses akmaya devam eder ve
  // donmuş görüntüden kaçar. Birlikte duraklat; dönüşte yeniden çapala.
  window.addEventListener('orientationchange', () => { if (sessionId) maybeRotateHint(); });
  // Tarayıcı bağlantının koptuğunu biliyorsa kalp atışını beklemeye gerek yok.
  window.addEventListener('offline', () => { if (sessionId && !audioIsPaused()) showNetHint(); });
  document.addEventListener('visibilitychange', () => {
    if (!sessionId) return;
    if (document.hidden) {
      // Gizlenen sekmede zamanlayıcılar ve akış askıya alınabilir: dönüşte
      // yerinde sürdürme yerine yeniden çapalama yolu kullanılır.
      userPausedAt = 0;
      if (!audioIsPaused()) { audioWantsPlay = true; pauseAudio(); }
    } else {
      lastDrawWall = 0;
      netGateAt = performance.now();
      forcePaint = true; // gizliyken tuval içeriği düşmüş olabilir
      void onVisible();
    }
  });
  // Oturum sunucuda düştü (401): yönlendirici girişe götürecek; oynatıcı kapanır.
  window.addEventListener(UNAUTHORIZED_EVENT, () => { if (root.isConnected) closePlayer(); });

  return {
    root, canvas, ctx, veil, veilText, title, subtitle, quota: quotaTag, rotate, netHint, controls, playBtn, seek,
    timeNow, timeEnd, volume, qualityBtn, channelBtn, stats, skipHint, overlay, menuHost,
  };
}

/**
 * Sekme yeniden görünür oldu. Arka plandayken zamanlayıcılar kısılır (telefonda
 * tümüyle durur) ve kira dolmuş olabilir: önce sunucuya sorulur. Oturum
 * kapanmışsa `beat` → `handleServerStop` bunu not eder ve aşağıdaki devam
 * çağrısı kaldığı yerden YENİ oturum açar; eskiden "ileri alınamadı" bildirimi
 * ve ardından "Yayın durduruldu" ekranı çıkıyordu.
 */
async function onVisible(): Promise<void> {
  const id = sessionId;
  if (!id) return;
  // Ağ ölüyse kalp atışı zaman aşımına kadar bekletmesin.
  if (!serverEnded) await Promise.race([beat(), sleep(2500)]);
  if (document.hidden || sessionId !== id) return;
  if (!audioWantsPlay) return;
  void acquireWakeLock();
  if (!audioGate) resumePlayback();
}

function togglePlay(): void {
  if (!sessionId) return;
  // Kapı kapalıyken ya da seek yoldayken yalnız niyet değişir; akış hazır olunca
  // o niyetle başlar. (Seek yoldayken "sürdür" sayılıp ikinci bir seek atılıyordu.)
  if (audioGate || pendingSeekSec !== null) {
    audioWantsPlay = !audioWantsPlay;
    if (pendingSeekSec !== null) seekIntent = audioWantsPlay;
    setPlayIcon(audioWantsPlay);
    resetIdle();
    return;
  }
  if (audioIsPaused()) {
    audioWantsPlay = true;
    if (canFastResume()) fastResume();
    else resumePlayback();
    setPlayIcon(true);
    void acquireWakeLock();
    track('resume', { ...playbackRef(), value: Math.round(positionSec()) });
  } else {
    // Niyeti de kaydet: aksi hâlde sekmeye dönüşte görünürlük işleyicisi
    // kullanıcının duraklattığı yayını kendiliğinden başlatıyordu.
    audioWantsPlay = false;
    pauseAudio();
    userPausedAt = performance.now();
    setPlayIcon(false);
    pauseCount += 1;
    releaseWakeLock();
    track('pause', { ...playbackRef(), value: Math.round(positionSec()) });
  }
  resetIdle();
}

/**
 * Kısa bir duraklatmadan sonra akış yerinde sürdürülebilir mi? Ses bağlamı
 * askıdayken saati de durur; okunmayan yanıt sunucudaki ffmpeg'i bekletir.
 * Hiçbir şey yıkılmadıysa devam etmek için bağlamı sürdürmek yeter. Eskiden her
 * devam sunucuda yeniden çapalama (seek) demekti: 1–2 sn perde ve anahtar
 * kareye yaslandığı için birkaç saniye geriye sıçrama.
 */
function canFastResume(): boolean {
  return mode === 'vod' && !!sessionId && audioClockRunning && audioPaused && !audioGate
    && pendingSeekSec === null && !serverEnded && !streamLost && !document.hidden
    && userPausedAt > 0 && performance.now() - userPausedAt < FAST_RESUME_MAX_MS;
}

function fastResume(): void {
  userPausedAt = 0;
  // Takılma bekçisi duraklatma süresini takılma saymasın.
  lastProgressWall = performance.now();
  resumeAudio();
}

/** VOD'da duraklatmadan dönüşte boruları yeniden kur: askıya alınmış fetch ve
 *  çözücü kuyrukları görünürlük geçişini sağ çıkmasın. */
function resumePlayback(): void {
  if (audioGate) { audioWantsPlay = true; return; }
  // Seek zaten yolda: bitince bu niyetle başlar, ikincisine gerek yok.
  if (pendingSeekSec !== null) return;
  // Canlı oturum biz beklerken sunucuda kapandı: canlı ucundan yeniden aç.
  // Canlıda akış da beklerken kopmuş olabilir; seek olmadığı için tek yol bu.
  if ((serverEnded || streamLost) && sessionId && mode !== 'vod') {
    const next = resumeTarget();
    if (next) void start(next, false);
    return;
  }
  // Akış kurulu ama saat hiç başlamadı (kapı duraklatılmışken açıldı): yeniden
  // çapalamaya gerek yok, bekleyen sesi planlamak yeter.
  if (sessionId && audioAbort && !audioClockRunning && !audioPaused && !serverEnded && !streamLost) {
    ensureAudioCtx();
    pumpAudio();
    return;
  }
  // Konum tek kaynaktan: duraklatılmışsa duraklatma anı, saat yoksa taban.
  if (mode === 'vod' && sessionId) void seekTo(positionSec());
  else { resumeAudio(); hardResync(); }
}

async function changeQuality(q: number): Promise<void> {
  if (q === state.quality) return;
  track('quality_change', { ...playbackRef(), quality: q, data: { from: state.quality } });
  setQuality(q);
  if (ui) ui.qualityBtn.replaceChildren(el('span', {}, t('player.ctl.qualityLabel', { q })), iconEl('chevronDown'));
  if (!target || !sessionId) return;
  const position = positionSec();
  const next: PlaybackTarget = { ...target, startAt: mode === 'vod' ? position : undefined };
  await start(next, true);
}

function toggleFullscreen(node: HTMLElement): void {
  const doc = document as any;
  if (!document.fullscreenElement && !doc.webkitFullscreenElement) {
    const request = node.requestFullscreen ?? (node as any).webkitRequestFullscreen;
    const enabled = document.fullscreenEnabled ?? doc.webkitFullscreenEnabled ?? true;
    if (!request || !enabled) { fullscreenUnavailable(); resetIdle(); return; }
    // İstek reddedilebilir (araç tarayıcısı, gömülü görünüm): sessizce hiçbir
    // şey olmaması "düğme çalışmıyor" demekti.
    try {
      const pending = request.call(node) as Promise<void> | undefined;
      pending?.catch?.(() => fullscreenUnavailable());
    } catch { fullscreenUnavailable(); }
    track('fullscreen', { ...playbackRef(), data: { on: true } });
  } else {
    track('fullscreen', { ...playbackRef(), data: { on: false } });
    const exit = document.exitFullscreen ?? doc.webkitExitFullscreen;
    if (exit) void exit.call(document);
  }
  resetIdle();
}

/**
 * Tarayıcı tam ekrana izin vermiyor. Tesla'nın tarayıcısı sayfalara tam ekranı
 * yalnız "Tiyatro" kipinde açar ve o kipe bir sayfa ancak YouTube'un
 * yönlendirme adresinden girebilir; araç park hâlinde olmalıdır. Yapabildiğimiz
 * şey bunu söylemek ve o yoldan yeniden açmak. Kaldığı yer kapanışta kaydedilir.
 */
function fullscreenUnavailable(): void {
  if (!isTeslaLike()) {
    toast(t('player.fullscreenUnsupported'), 'info', 6000);
    return;
  }
  void confirmDialog({
    title: t('player.teslaFullTitle'),
    body: t('player.teslaFullBody'),
    confirmLabel: t('player.teslaFullConfirm'),
    cancelLabel: t('common.cancel'),
  }).then((ok) => {
    if (!ok) return;
    closePlayer();
    // Konum kaydı (DELETE) yola çıksın diye kısa bir pay.
    window.setTimeout(() => {
      location.href = 'https://www.youtube.com/redirect?q=' + encodeURIComponent(location.origin + '/browse');
    }, 350);
  });
}

/** Tanılama: görüntü ile ses ayrıldığında asıl soru "video geç mi geliyor
 *  (yastık sıfır) yoksa geç mi çözülüyor (kuyruk dolu)" — koltuktan ikisi aynı
 *  görünür. Başlığa dokununca açılır. */
function updateStats(): void {
  if (!statsOn || !ui) return;
  const now = performance.now();
  if (now - statsAt < 500) return;
  const fps = statsAt ? (drawnFrames - statsFrames) / ((now - statsAt) / 1000) : 0;
  statsAt = now;
  statsFrames = drawnFrames;
  const queue = videoDecoder ? videoDecoder.decodeQueueSize : 0;
  ui.stats.textContent =
    `A/V ${lastDrawLagMs >= 0 ? '+' : ''}${Math.round(lastDrawLagMs)} ms\n` +
    `tampon ${(leadMs() / 1000).toFixed(1)} sn · bekleyen ${pendingChunks.length} · kuyruk ${queue}\n` +
    `${fps.toFixed(0)} fps · ${resyncs} yeniden çapa · donma ${Math.round(maxDrawGapMs)} ms\n` +
    `${state.quality}p · ses ${pendingAudio.length} · derleme ${state.buildId.slice(-6)}`;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
